import { Button, Drawer, Icon, Toggle } from '@teamsnap/teamsnap-ui';
import * as React from 'react';
import * as ShowOrganization from 'core/gql/queries/show-organizations';
import * as ListInstallmentPlans from 'core/gql/queries/list-installment-plans';
import * as UpdateInstallmentPlan from 'core/gql/mutations/update-installment-plan';
import { InstallmentPlan } from './types';
import './Installments.scss';
import InstallmentsDrawerForm from './InstallmentsDrawer';
import { useMutation, useQuery } from '@apollo/client';
import { getDepositDescription, getInstallmentsDescription } from './installment-utils';
import { reactiveFeedbackMessage } from 'core/apollo-cache';

interface Props {
  scopeId: number;
  scopeType: string;
  orgId: number;
}

const Installments: React.FC<Props> = ({ scopeId, scopeType, orgId }) => {
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [editingPlan, setEditingPlan] = React.useState<InstallmentPlan | null | undefined>();

  const showOrgQuery = ShowOrganization.QUERY;

  const getOrganization = useQuery(showOrgQuery, {
    variables: { organizationId: +orgId },
    skip: !orgId,
  });

  const getInstallmentPlans = useQuery(ListInstallmentPlans.QUERY, {
    variables: { scopeId, scopeType },
    skip: !scopeId || !scopeType,
  });

  const [updateInstallmentPlan] = useMutation(UpdateInstallmentPlan.MUTATION);

  const organization = getOrganization.data?.showOrganization;
  const plans = getInstallmentPlans.data?.listInstallmentPlans || [];

  React.useEffect(() => {
    // makes sure the plan is updated before the form mounts
    if (editingPlan === undefined) {
      setShowDrawer(false);
    } else {
      setShowDrawer(true);
    }
  }, [editingPlan]);

  const closeDrawer = () => {
    setEditingPlan(undefined);
  };

  const renderPlans = React.useCallback(() => {
    const toggleEnablePlan = async (plan: InstallmentPlan, enabled: boolean) => {
      const updatedPlan = { ...plan, enabled };

      try {
        await updateInstallmentPlan({ variables: updatedPlan });
      } catch (error) {
        console.error(error);
        reactiveFeedbackMessage({
          message: 'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
          isError: true,
          autoclose: true,
        });
      }
    };

    return plans.map((plan) => (
      <div className="Installments__plan-list-item u-spaceBottomSm" key={plan.id}>
        <div className="Installments__plan-list-header u-flex u-flexJustifyBetween u-flexAlignItemsBaseline">
          <Toggle
            name={plan.id}
            inputProps={{ defaultChecked: plan.enabled, onChange: () => toggleEnablePlan(plan, !plan.enabled) }}
          />
          <span className="u-flexGrow1 u-spaceLeftMd" title={plan.name}>{plan.name}</span>
          <button
            className="Installments__plan-list-icon-button"
            onClick={() => {
              setEditingPlan(plan);
            }}
          >
            {/* TODO use snap-ui Icons */}
            <Icon name="edit" />
          </button>
        </div>
        <div className="u-padMd">
          <div className="u-spaceBottomSm">
            Down Payment: {getDepositDescription(plan, organization?.currencySymbol)}
          </div>
          <div className="">{getInstallmentsDescription(plan)}</div>
        </div>
      </div>
    ));
  }, [plans, updateInstallmentPlan, organization]);

  return (
    <>
      <Drawer
        open={showDrawer}
        allowOverlayClose={false}
        style={{ maxWidth: '730px' }}
        closeFn={closeDrawer}
        mods="u-sizeFull"
        showOverlay={true}
        allowBodyScroll={false}
      >
        {showDrawer && ( // mounting/unmounting inits the hook form fully
          <InstallmentsDrawerForm
            scopeId={scopeId}
            scopeType={scopeType}
            isOpen={showDrawer}
            onClose={closeDrawer}
            plan={editingPlan}
            currency={organization?.currency || ''}
            currencySymbol={organization?.currencySymbol || ''}
          />
        )}
      </Drawer>

      <div id="installments" className="u-flex u-flexCol">
        <div className="u-flex u-flexAlignSelfEnd u-spaceBottomMd">
          <Button
            type="button"
            mods="u-padSidesLg"
            color="primary"
            onClick={() => {
              setEditingPlan(null);
            }}
            icon="plus"
            style={{ height: 44 }}
          >
            Add payment plan
          </Button>
        </div>

        {plans.length > 0 ? <>{renderPlans()}</> : <p>No payment plans created.</p>}
      </div>
    </>
  );
};

export default Installments;
